import { ticketApi } from "@/api/tickets";
import { useTicketSearchStore } from "@/store/ticketSearch";
import { TICKET_KEYS } from "@/queries/tickets";
import { TICKET_LIST_KEYS } from "@/queries/ticketLists";
import { defineQuery, useQuery, useQueryCache } from "@pinia/colada";
import { computed } from "vue";

export const TICKET_SEARCH_KEYS = {
    root: ["ticketSearch"] as const,
    results: (query: string, page: number | null, perPage: number) =>
        [...TICKET_SEARCH_KEYS.root, "results", { query, page, perPage }] as const,
    deleted: (query: string, page: number | null, perPage: number) =>
        [...TICKET_SEARCH_KEYS.root, "deleted", { query, page, perPage }] as const,
};

function searchQuery(deleted: boolean) {
    return () => {
        const searchStore = useTicketSearchStore();
        const keys = deleted ? TICKET_SEARCH_KEYS.deleted : TICKET_SEARCH_KEYS.results;
        const query = useQuery({
            key: () =>
                keys(
                    searchStore.submittedQuery,
                    searchStore.options.page,
                    searchStore.effectivePerPage
                ),
            query: () => {
                const request = searchStore.submitted;
                if (request == null) {
                    return Promise.reject(new Error("No search has been submitted."));
                }
                return deleted
                    ? ticketApi.searchDeleted(
                          request,
                          searchStore.options.page,
                          searchStore.effectivePerPage
                      )
                    : ticketApi.search(
                          request,
                          searchStore.options.page,
                          searchStore.effectivePerPage
                      );
            },
            enabled: () => searchStore.submitted != null,
            // Cached results render instantly; any remount revalidates them.
            staleTime: 0,
            // Paging and re-searching keep the previous rows under the refresh
            // indicator instead of blanking the list.
            placeholderData: (previousData) => previousData,
        });
        return {
            ...query,
            results: computed(() => query.state.value.data?.tickets ?? []),
            total: computed(() => query.state.value.data?.total ?? 0),
        };
    };
}

export const useTicketSearchResults = defineQuery(searchQuery(false));
export const useDeletedTicketSearchResults = defineQuery(searchQuery(true));

/**
 * Revalidates every cached ticket surface: search results, details, hover
 * summaries, lookups (bulk edits can mint labels) and ticket lists. Active
 * views refetch in place; the rest refetch when next shown. A ticket shows up
 * on more surfaces than the one it was edited from, so both bulk operations
 * and single-ticket edits need all of them refreshed.
 */
export async function invalidateTicketData(): Promise<void> {
    const queryCache = useQueryCache();

    await Promise.all([
        queryCache.invalidateQueries({ key: TICKET_SEARCH_KEYS.root }),
        queryCache.invalidateQueries({ key: TICKET_KEYS.root }),
        queryCache.invalidateQueries({ key: TICKET_LIST_KEYS.root }),
    ]);
}
